<template>
  <span class="ctt" @mouseenter="show" @mouseleave="hide" @focusin="show" @focusout="hide">
    <slot />
  </span>
</template>

<script>
/**
 * Custom hover tooltip (no native title, no vue-material).
 * The bubble is appended to <body> and positioned with fixed coordinates from
 * the trigger's bounding box, so it is never clipped by scrolling tables or
 * trapped by transformed ancestors (md-app-container). Shown above the
 * trigger, flips below when there is no room. Hidden on scroll/resize.
 * Usage: <CustomTooltip :text="hint"><anything hoverable /></CustomTooltip>
 */
const GAP = 8;
const EDGE = 8;

export default {
  name: 'CustomTooltip',
  props: {
    text: { type: String, default: '' }
  },
  data() {
    return { bubble: null };
  },
  watch: {
    text(value) {
      if (this.bubble) this.bubble.textContent = value;
    }
  },
  beforeDestroy() {
    this.hide();
  },
  methods: {
    show() {
      if (!this.text || this.bubble) return;
      const bubble = document.createElement('div');
      bubble.className = 'ctt__bubble';
      bubble.setAttribute('role', 'tooltip');
      bubble.textContent = this.text;
      document.body.appendChild(bubble);
      this.bubble = bubble;
      this.position();
      window.addEventListener('scroll', this.hide, true);
      window.addEventListener('resize', this.hide);
    },
    position() {
      const trigger = this.$el.getBoundingClientRect();
      const b = this.bubble.getBoundingClientRect();
      const vw = window.innerWidth;
      let left = trigger.left + trigger.width / 2 - b.width / 2;
      left = Math.min(Math.max(left, EDGE), Math.max(EDGE, vw - b.width - EDGE));
      let top = trigger.top - GAP - b.height;
      let below = false;
      if (top < EDGE) { top = trigger.bottom + GAP; below = true; }
      this.bubble.style.left = left + 'px';
      this.bubble.style.top = top + 'px';
      this.bubble.classList.toggle('ctt__bubble--below', below);
      // keep the arrow on the trigger's centre, inside the bubble's rounding
      const arrowX = Math.min(Math.max(trigger.left + trigger.width / 2 - left, 10), b.width - 10);
      this.bubble.style.setProperty('--ctt-arrow-x', arrowX + 'px');
    },
    hide() {
      if (!this.bubble) return;
      this.bubble.remove();
      this.bubble = null;
      window.removeEventListener('scroll', this.hide, true);
      window.removeEventListener('resize', this.hide);
    }
  }
};
</script>

<style>
/* global on purpose: the bubble lives in <body>, outside this component's scope */
.ctt { display: inline-flex; align-items: center; }
.ctt__bubble {
  position: fixed;
  z-index: 100000000002;
  max-width: 280px;
  padding: 7px 11px;
  border-radius: 8px;
  background: #343841;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: 0.2px;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(20, 30, 45, 0.25);
  pointer-events: none;
  animation: ctt-in 0.12s ease-out;
}
.ctt__bubble::after {
  content: '';
  position: absolute;
  left: var(--ctt-arrow-x, 50%);
  top: 100%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #343841;
}
.ctt__bubble--below::after {
  top: auto;
  bottom: 100%;
  border-top-color: transparent;
  border-bottom-color: #343841;
}
@keyframes ctt-in { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: none; } }
</style>
